import { Opportunity, OPPORTUNITY_STATUSES } from '@/hooks/useOpportunities';

interface OpportunityExportCardProps {
  opportunity: Opportunity;
}

const BASE_FONT = '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, "Helvetica Neue", Arial, sans-serif';

// Cached favicon storage URL
const STORAGE_BASE = 'https://fxlorumfwzrdwiftbalo.supabase.co/storage/v1/object/public/cached-favicons';

function getStorageUrl(domain: string): string {
  const fileName = `${domain.replace(/[^a-zA-Z0-9]/g, '_')}.png`;
  return `${STORAGE_BASE}/${fileName}`;
}

function extractDomain(website: string | null | undefined): string | null {
  if (!website) return null;
  return website.replace(/^https?:\/\//, '').replace(/^www\./, '').split('/')[0];
}

export function OpportunityExportCard({ opportunity }: OpportunityExportCardProps) {
  const status = OPPORTUNITY_STATUSES.find(s => s.id === opportunity.status);
  
  const getOrgIcon = () => {
    // Priority 1: Cached favicon URL from database (CORS-safe)
    const cachedUrl = opportunity.organization?.cached_favicon_url;
    if (cachedUrl) {
      return (
        <img
          src={cachedUrl}
          alt=""
          style={{ 
            width: '16px', 
            height: '16px', 
            objectFit: 'contain', 
            flexShrink: 0,
            borderRadius: '2px',
          }}
          crossOrigin="anonymous"
          onError={(e) => { (e.target as HTMLImageElement).style.display = 'none'; }}
        />
      );
    }
    
    // Priority 2: Try storage URL based on domain
    const domain = extractDomain(opportunity.organization?.website);
    if (domain) {
      return (
        <img
          src={getStorageUrl(domain)}
          alt=""
          style={{ 
            width: '16px', 
            height: '16px', 
            objectFit: 'contain', 
            flexShrink: 0,
            borderRadius: '2px',
          }}
          crossOrigin="anonymous"
          onError={(e) => { (e.target as HTMLImageElement).style.display = 'none'; }}
        />
      );
    }
    
    // Priority 3: Building icon fallback (inline SVG for html2canvas compatibility)
    return (
      <svg 
        width="16" 
        height="16" 
        viewBox="0 0 24 24" 
        fill="none" 
        stroke="#6b7280" 
        strokeWidth="2"
        strokeLinecap="round"
        strokeLinejoin="round"
        style={{ flexShrink: 0 }}
      >
        <rect x="4" y="2" width="16" height="20" rx="2" ry="2"/>
        <path d="M9 22v-4h6v4"/>
        <path d="M8 6h.01"/>
        <path d="M16 6h.01"/>
        <path d="M12 6h.01"/>
        <path d="M12 10h.01"/>
        <path d="M12 14h.01"/>
        <path d="M16 10h.01"/>
        <path d="M16 14h.01"/>
        <path d="M8 10h.01"/>
        <path d="M8 14h.01"/>
      </svg>
    );
  };

  return (
    <div 
      style={{
        backgroundColor: 'white',
        borderRadius: '8px',
        border: '1px solid #e5e7eb',
        borderLeft: `4px solid ${status?.color || '#9ca3af'}`,
        padding: '12px',
        marginBottom: '8px',
        fontFamily: BASE_FONT,
        fontStyle: 'normal',
      }}
    >
      {/* Name - bold and larger for better visibility */}
      <div style={{ 
        fontWeight: 700, 
        fontSize: '14px', 
        color: '#111827',
        marginBottom: '8px',
        lineHeight: '1.5',
        fontFamily: BASE_FONT,
        fontStyle: 'normal',
      }}>
        {opportunity.name}
      </div>

      {/* Organization - darker text with better weight */}
      {opportunity.organization && (
        <div style={{ 
          display: 'flex', 
          alignItems: 'center', 
          gap: '6px', 
          fontSize: '13px', 
          color: '#374151',
          marginBottom: '8px',
        }}>
          {getOrgIcon()}
          <span style={{ 
            lineHeight: '1.5',
            fontWeight: 500,
            fontFamily: BASE_FONT,
            fontStyle: 'normal',
          }}>
            {opportunity.organization.name}
          </span>
        </div>
      )}

      {/* Locations */}
      {opportunity.opportunity_locations && opportunity.opportunity_locations.length > 0 && (
        <div style={{ 
          display: 'flex', 
          alignItems: 'center', 
          gap: '6px', 
          fontSize: '11px', 
          color: '#6b7280',
          marginBottom: '6px',
        }}>
          {/* MapPin inline SVG */}
          <svg 
            width="12" 
            height="12" 
            viewBox="0 0 24 24" 
            fill="none" 
            stroke="#3b82f6" 
            strokeWidth="2"
            strokeLinecap="round"
            strokeLinejoin="round"
            style={{ flexShrink: 0 }}
          >
            <path d="M20 10c0 6-8 12-8 12s-8-6-8-12a8 8 0 0 1 16 0Z"/>
            <circle cx="12" cy="10" r="3"/>
          </svg>
          <span style={{
            fontFamily: BASE_FONT,
            fontStyle: 'normal',
          }}>
            {opportunity.opportunity_locations.length === 1 
              ? opportunity.opportunity_locations[0].location?.name || 'Locatie'
              : `${opportunity.opportunity_locations.length} locaties`}
          </span>
        </div>
      )}

      {/* Tags */}
      {opportunity.tags && opportunity.tags.length > 0 && (
        <div style={{ display: 'flex', flexWrap: 'wrap', gap: '4px', marginBottom: '6px' }}>
          {opportunity.tags.slice(0, 2).map((tag) => (
            <span 
              key={tag}
              style={{
                fontSize: '10px',
                padding: '2px 6px',
                borderRadius: '4px',
                backgroundColor: '#f3f4f6',
                color: '#374151',
                fontFamily: BASE_FONT,
                fontStyle: 'normal',
              }}
            >
              {tag}
            </span>
          ))}
          {opportunity.tags.length > 2 && (
            <span style={{
              fontSize: '10px',
              padding: '2px 6px',
              borderRadius: '4px',
              backgroundColor: '#f3f4f6',
              color: '#374151',
              fontFamily: BASE_FONT,
              fontStyle: 'normal',
            }}>
              +{opportunity.tags.length - 2}
            </span>
          )}
        </div>
      )}

      {/* Fleet stats - centered */}
      {(opportunity.total_fleet || opportunity.electric_trucks) && (
        <div style={{ 
          display: 'flex', 
          alignItems: 'center', 
          justifyContent: 'center',
          gap: '16px', 
          fontSize: '12px',
          paddingTop: '6px',
          borderTop: '1px solid #f3f4f6',
        }}>
          {opportunity.total_fleet && (
            <span style={{ 
              display: 'flex', 
              alignItems: 'center', 
              gap: '4px', 
              color: '#6b7280',
              fontFamily: BASE_FONT,
              fontStyle: 'normal',
            }}>
              {/* Truck inline SVG */}
              <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <path d="M14 18V6a2 2 0 0 0-2-2H4a2 2 0 0 0-2 2v11a1 1 0 0 0 1 1h2"/>
                <path d="M15 18h2a1 1 0 0 0 1-1v-3.28a1 1 0 0 0-.684-.948l-1.923-.641a1 1 0 0 1-.684-.948V8.5A1.5 1.5 0 0 1 17.209 7H19a2 2 0 0 1 2 2v8a1 1 0 0 1-1 1h-2"/>
                <circle cx="7" cy="18" r="2"/>
                <circle cx="17" cy="18" r="2"/>
              </svg>
              {opportunity.total_fleet}
            </span>
          )}
          {opportunity.electric_trucks && (
            <span style={{ 
              display: 'flex', 
              alignItems: 'center', 
              gap: '4px', 
              color: '#16a34a',
              fontFamily: BASE_FONT,
              fontStyle: 'normal',
            }}>
              {/* Zap inline SVG */}
              <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <path d="M13 2 3 14h9l-1 8 10-12h-9l1-8z"/>
              </svg>
              {opportunity.electric_trucks}
            </span>
          )}
        </div>
      )}
    </div>
  );
}
